import { ExclusionAreaGeoJSON } from '../../../types/apiModels'

export type EditState =
  | { mode: 'idle' }
  | { mode: 'drawing' }
  | {
      mode: 'editing'
      editingItemId: string
      selectedArea: ExclusionAreaGeoJSON
      originalArea: ExclusionAreaGeoJSON
    }
  | {
      mode: 'editingGeometry'
      editingItemId: string
      selectedArea: ExclusionAreaGeoJSON
      originalArea: ExclusionAreaGeoJSON
    }
  | {
      mode: 'editingAndDrawing'
      editingItemId: string
      selectedArea: ExclusionAreaGeoJSON
      originalArea: ExclusionAreaGeoJSON
    }

export type EditAction =
  | { type: 'START_DRAWING' }
  | {
      type: 'START_EDITING'
      payload: {
        editingItemId: string
        area: ExclusionAreaGeoJSON
      }
    }
  | {
      type: 'START_EDITING_GEOMETRY'
      payload: {
        editingItemId: string
        area: ExclusionAreaGeoJSON
      }
    }
  | { type: 'START_DRAWING_ON_SELECTED' }
  | { type: 'STOP_DRAWING_ON_SELECTED' }
  | {
      type: 'UPDATE_SELECTED_AREA'
      payload: { area: ExclusionAreaGeoJSON }
    }
  | { type: 'CANCEL_EDIT' }
  | { type: 'FINISH_EDIT' }
  | { type: 'RESET' }

export function editStateReducer(state: EditState, action: EditAction): EditState {
  switch (action.type) {
    case 'START_DRAWING':
      return { mode: 'drawing' }

    case 'START_EDITING': {
      const { area, editingItemId } = action.payload
      const areaWithSelected = {
        ...area,
        properties: {
          ...area.properties,
          selected: true,
        },
      }
      return {
        mode: 'editing',
        editingItemId,
        selectedArea: areaWithSelected,
        originalArea: structuredClone(area),
      }
    }

    case 'START_EDITING_GEOMETRY': {
      const { area, editingItemId } = action.payload
      return {
        mode: 'editingGeometry',
        editingItemId,
        selectedArea: area,
        originalArea: structuredClone(area),
      }
    }

    case 'START_DRAWING_ON_SELECTED':
      if (state.mode !== 'editingGeometry') {
        console.warn('Cannot start drawing on selected area when not in editingGeometry mode')
        return state
      }
      return {
        mode: 'editingAndDrawing',
        editingItemId: state.editingItemId,
        selectedArea: state.selectedArea,
        originalArea: state.originalArea,
      }

    case 'STOP_DRAWING_ON_SELECTED':
      if (state.mode !== 'editingAndDrawing') {
        console.warn('Cannot stop drawing when not in editingAndDrawing mode')
        return state
      }
      return {
        mode: 'editingGeometry',
        editingItemId: state.editingItemId,
        selectedArea: state.selectedArea,
        originalArea: state.originalArea,
      }

    case 'UPDATE_SELECTED_AREA':
      if (
        state.mode !== 'editing' &&
        state.mode !== 'editingGeometry' &&
        state.mode !== 'editingAndDrawing'
      ) {
        console.warn('Cannot update selected area when not in editing mode')
        return state
      }
      return {
        ...state,
        selectedArea: action.payload.area,
      }

    case 'CANCEL_EDIT':
    case 'FINISH_EDIT':
    case 'RESET':
      return { mode: 'idle' }

    default:
      return state
  }
}

export const editStateSelectors = {
  isDrawing: (state: EditState) => state.mode === 'drawing' || state.mode === 'editingAndDrawing',
  isEditing: (state: EditState) =>
    state.mode === 'editing' ||
    state.mode === 'editingGeometry' ||
    state.mode === 'editingAndDrawing',
  canEditGeometry: (state: EditState) =>
    state.mode === 'editingGeometry' || state.mode === 'editingAndDrawing',
  isIdle: (state: EditState) => state.mode === 'idle',
  getEditingItemId: (state: EditState) =>
    state.mode === 'editing' ||
    state.mode === 'editingGeometry' ||
    state.mode === 'editingAndDrawing'
      ? state.editingItemId
      : null,
  getSelectedArea: (state: EditState) =>
    state.mode === 'editing' ||
    state.mode === 'editingGeometry' ||
    state.mode === 'editingAndDrawing'
      ? state.selectedArea
      : null,
  getOriginalArea: (state: EditState) =>
    state.mode === 'editing' ||
    state.mode === 'editingGeometry' ||
    state.mode === 'editingAndDrawing'
      ? state.originalArea
      : null,
  getMapAction: (state: EditState): 'draw' | 'edit' | null => {
    if (state.mode === 'drawing') return 'draw'
    if (state.mode === 'editingGeometry' || state.mode === 'editingAndDrawing') return 'edit'
    return null
  },
}
